import { NavigationGuard } from "vue-router";
import { userModule } from "@/store/modules/user";
import { FormAction } from "@/utils/form-access";

/**
 * Router guard untuk gating route berdasarkan form access.
 *
 * Pemakaian: pasang di router.beforeEach (lihat router/index.ts).
 * Tambah meta di route:
 *   {
 *     path: '/persetujuan/general-consent',
 *     component: ...,
 *     meta: { formAccess: { code: 'general_consent_ranap', action: 'view' } }
 *   }
 *
 * Optional any-of: meta.formAccess = { code: 'x', actions: ['view','create'] }
 *   → user lolos kalau punya minimum salah satu action.
 *
 * Skip kalau user belum login (handled oleh requireAuth chain).
 */
const formAccessGuard: NavigationGuard = (to, _from, next) => {
  const matched = to.matched
    .map(r => (r.meta as any)?.formAccess)
    .filter(m => m && m.code);

  const matchedPrivilege = to.matched
    .map(r => (r.meta as any)?.privilege)
    .filter(m => m && m.domain);

  if (matched.length === 0 && matchedPrivilege.length === 0) {
    return next();
  }

  if (!userModule.isSignedIn) {
    return next();
  }

  for (const meta of matchedPrivilege) {
    if (!userModule.hasPrivilege(meta.domain, meta.action ?? "view")) {
      return next({
        path: "/forbidden",
        query: { form: meta.domain, action: meta.action ?? "view" }
      });
    }
  }

  for (const meta of matched) {
    const code: string = meta.code;
    const actions: FormAction[] =
      meta.actions ?? [meta.action ?? ("view" as FormAction)];
    const ok = actions.some(action =>
      userModule.canAccessForm(code, action)
    );
    if (!ok) {
      return next({
        path: "/forbidden",
        query: { form: code, action: actions[0] }
      });
    }
  }

  return next();
};

export default formAccessGuard;
